import { Booking, BookingStatus } from '@/domain/entities/booking.entity';
import { apiClient } from '@/data/api/axios-client';
import {
    BookingAddonDTO,
    BookingDetailResponseDTO,
    BookingPackageDTO,
    BookingRequestDTO,
    BookingResponseDTO,
    BookingResponseDataDTO,
    BookingThemeDTO,
} from '@/data/dto/booking.dto';

const mockBookings: Map<string, Booking> = new Map();
const BOOKING_STATUSES: BookingStatus[] = ['PENDING', 'APPROVED', 'REJECTED'];

const parseBookingStatus = (status: string): BookingStatus => {
    return BOOKING_STATUSES.includes(status as BookingStatus)
        ? (status as BookingStatus)
        : 'PENDING';
};

const mapPackage = (pkg?: BookingPackageDTO) => {
    if (!pkg) return undefined;

    return {
        id: pkg.id,
        module: pkg.module,
        name: pkg.name,
        description: pkg.description,
        price: pkg.price,
        basePrice: pkg.basePrice,
        discount: pkg.discount,
        durationMinutes: pkg.durationMinutes,
        finalPrice: pkg.finalPrice,
        isActive: pkg.isActive,
        offers: pkg.offers,
        imageUrl: pkg.imageUrl,
    };
};

const mapTheme = (theme?: BookingThemeDTO) => {
    if (!theme) return undefined;

    return {
        id: theme.id,
        module: theme.module,
        name: theme.name,
        description: theme.description,
        imageUrl: theme.imageUrl,
        price: theme.price,
        isActive: theme.isActive,
    };
};

const mapAddon = (addon?: BookingAddonDTO) => {
    if (!addon) return undefined;

    return {
        id: addon.id,
        module: addon.module,
        name: addon.name,
        description: addon.description,
        price: addon.price,
        unit: addon.unit,
        isActive: addon.isActive,
    };
};

const mapBookingResponse = (data: BookingResponseDataDTO): Booking => ({
    id: data.id,
    packageId: data.packageId,
    package: mapPackage(data.package),
    slots: (data.slots || []).map(slot => ({
        date: slot.date,
        time: slot.time,
        themeId: slot.themeId,
        theme: mapTheme(slot.theme),
    })),
    addons: (data.addons || []).map(addon => ({
        addonId: addon.addonId,
        quantity: addon.quantity,
        addon: mapAddon(addon.addon),
    })),
    customer: {
        name: data.customer?.name || '',
        email: data.customer?.email || '',
        phone: data.customer?.phone || '',
        notes: data.customer?.notes,
    },
    status: parseBookingStatus(data.status),
    totalPrice: data.totalPrice,
    createdAt: data.createdAt ? new Date(data.createdAt) : undefined,
    paymentScreenshotUrl: data.paymentScreenshotUrl,
});

/**
 * Repository for booking data.
 * Supports both real API and mock data via parameter.
 */
export const bookingRepository = {
    createBooking: async (booking: Booking, useMock: boolean = false): Promise<Booking> => {
        if (useMock) {
            await new Promise((resolve) => setTimeout(resolve, 1000));

            const id = `bk-${Math.random().toString(36).substr(2, 9)}`;
            const newBooking = {
                ...booking,
                id,
                status: 'PENDING' as const,
                createdAt: new Date(),
            };

            mockBookings.set(id, newBooking);
            console.log('[MockBookingRepository] Booking created:', newBooking);

            return newBooking;
        }

        const endpoint = process.env.NEXT_PUBLIC_BOOKINGS || '/api/bookings';

        const requestBody: BookingRequestDTO = {
            packageId: booking.packageId,
            slots: booking.slots.map(slot => ({
                date: slot.date,
                time: slot.time,
                themeId: slot.themeId
            })),
            addons: booking.addons.map(addon => ({
                addonId: addon.addonId,
                quantity: addon.quantity
            })),
            customer: {
                name: booking.customer.name,
                email: booking.customer.email,
                phone: booking.customer.phone,
                notes: booking.customer.notes
            }
        };

        const response = await apiClient.post<BookingResponseDTO>(endpoint, requestBody);

        if (!response.success) {
            throw new Error(response.message || 'Failed to create booking');
        }

        return mapBookingResponse(response.data);
    },

    getBookingById: async (id: string, useMock: boolean = false): Promise<Booking | null> => {
        if (useMock) {
            await new Promise((resolve) => setTimeout(resolve, 500));
            return mockBookings.get(id) || null;
        }

        const endpoint = `${process.env.NEXT_PUBLIC_BOOKINGS || '/api/bookings'}/${id}`;

        try {
            const response = await apiClient.get<BookingDetailResponseDTO>(endpoint);

            if (response.success && response.data) {
                return mapBookingResponse(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch booking details:', error);
            return null;
        }

        return null;
    },
};
